import { UUID } from "crypto";
import { baseApi } from "./baseApi";

import {
    AnalysisVisit,
    Application,
    ApplicationDetail,
    ApplicationMarketplaceResume,
    ApplicationPaymentMethod,
    ApplicationResume,
    BankAccount,
    CertificateTax,
    CheckControl,
    Comment,
    CompanyProfile,
    Condition,
    CreditValidation,
    DestinationResource,
    FinancialOffer,
    LegalIncident,
    ScheduleVisit,
    Guarantee,
    LeasingRecord,
    MotorbikeDetail,
    BankAccountDocument,
} from "@interfaces/application";
import { PaginatedObject, Response, ResponsePagination } from "@interfaces/response";

import CompanyProfileData from "../dataDummy/companyProfile.json";
import ConditionData from "../dataDummy/condition.json";
import ApplicationData from "../dataDummy/application.json";
import CsfData from "../dataDummy/csf.json";
import CuentasBancarias from "../dataDummy/cuentasBancarias.json";
import ValidacionCreditia from "../dataDummy/validacionCrediticia.json";
import ScheduleVisitData from "../dataDummy/scheduleVisitData.json";

const applicationsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getApplication: build.query<Application, UUID>({
            // query: (applicationId) => ({
            //     url: `solicitudes`,
            //     params: {
            //         application_id: applicationId,
            //     },
            // }),
            // transformResponse: (response: Response<Application>) => {
            //     return response.data.at(0);
            // },

            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args === "3912e960-7795-434f-bc47-d277ff43937e") {
                    return {
                        data: ApplicationData,
                    };
                }
                const result = await baseQuery({
                    url: `solicitudes`,
                    params: {
                        application_id: args,
                    },
                });

                return {
                    data: result.data.data.at(0),
                };
            },
        }),
        getApplicationResume: build.query<PaginatedObject<ApplicationResume>, string>({
            query: (params) => ({
                url: `solicitudes/resume?${params}`,
            }),
            transformResponse: (response: ResponsePagination<ApplicationResume>) => {
                return response.data;
            },
        }),
        getApplicationResumeByCompanyId: build.query<ApplicationResume, { companyId: number; params: string }>({
            query: ({ companyId, params }) => ({
                url: `solicitudes/resume/${companyId}?${params}`,
            }),
            transformResponse: (response: Response<ApplicationResume>) => {
                return response.data;
            },
        }),
        getApplicationsMarketplaceResume: build.query<PaginatedObject<ApplicationMarketplaceResume>, string>({
            query: (params) => ({
                url: `solicitudes/marketplace/resume?${params}`,
            }),
            transformResponse: (response: ResponsePagination<ApplicationMarketplaceResume>) => {
                return response.data;
            },
        }),
        putApplications: build.mutation<
            any,
            {
                applicationId: UUID | undefined;
                body: Partial<Application> & { user_name?: string; user_id_modified?: UUID };
            }
        >({
            query: ({ applicationId, body }) => ({
                url: `solicitudes/${applicationId}`,
                method: "PUT",
                body: body,
            }),
        }),
        getScheduledVisit: build.query<Array<ScheduleVisit>, UUID | undefined>({
            // query: (applicationId) => ({
            //     url: `solicitudes/${applicationId}/agendar_visita`,
            // }),
            // transformResponse: (response: ResponsePagination<ScheduleVisit>) => {
            //     return response.data.data;
            // },
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args === "3912e960-7795-434f-bc47-d277ff43937e") {
                    return {
                        data: ScheduleVisitData.data,
                    };
                }
                const result = await baseQuery({
                    url: `solicitudes/${args}/agendar_visita`,
                });

                return result.data.data;
            },
        }),
        postScheduledVisit: build.mutation<
            ScheduleVisit,
            {
                appId: UUID | undefined;
                timestamp?: string;
                personId?: UUID;
                visitorId?: UUID;
                visitorName?: string | null;
                willVisitProspect?: boolean;
            }
        >({
            query: ({ appId, timestamp, personId, visitorId, visitorName, willVisitProspect }) => ({
                url: `solicitudes/${appId}/agendar_visita`,
                method: "POST",
                body: {
                    loan_application_id: appId,
                    date: timestamp ?? null,
                    date_change: false,
                    active: true,
                    ...(personId && { person_id: personId }),
                    ...(visitorId && { visitor_id: visitorId }),
                    ...(visitorName && { visitor_name: visitorName }),
                    ...(willVisitProspect !== undefined && { will_visit_prospect: willVisitProspect }),
                },
            }),
            transformResponse: (response: Response<ScheduleVisit>) => response.data,
        }),
        patchScheduledVisit: build.mutation<
            ScheduleVisit,
            { appId: UUID | undefined; scheduleVisitId: UUID | undefined; body: Partial<ScheduleVisit> }
        >({
            query: ({ appId, scheduleVisitId, body }) => ({
                url: `solicitudes/${appId}/agendar_visita/${scheduleVisitId}`,
                method: "PATCH",
                body: {
                    loan_application_id: appId,
                    ...body,
                },
            }),
            transformResponse: (response: Response<ScheduleVisit>) => response.data,
        }),
        postLostInterest: build.mutation<any, { applicationId: UUID; razon_uninterested_id: UUID }>({
            query: ({ applicationId, razon_uninterested_id }) => ({
                url: `solicitudes/${applicationId}/perdida_interes`,
                method: "POST",
                body: {
                    uninterest_reasons: [
                        {
                            razon_uninterested_id: razon_uninterested_id,
                        },
                    ],
                },
            }),
        }),
        postReasonRejectApp: build.mutation<
            any,
            {
                applicationId: UUID | undefined;
                payload: any;
            }
        >({
            query: ({ applicationId, payload }) => ({
                url: `solicitudes/${applicationId}/razones_rechazo`,
                method: "POST",
                body: { rejection_reasons: payload },
            }),
        }),
        getCommentsByApp: build.query<Array<Comment>, UUID>({
            query: (applicationId) => ({
                url: `solicitudes/${applicationId}/comentarios`,
            }),
            transformResponse: (response: ResponsePagination<Comment>) => {
                return response.data.data;
            },
        }),
        postCommentByApp: build.mutation<any, { applicationId: UUID; payload: unknown }>({
            query: ({ applicationId, payload }) => ({
                url: `solicitudes/${applicationId}/comentarios`,
                method: "POST",
                body: payload,
            }),
        }),
        deleteCommentByApp: build.mutation<any, { applicationId: UUID; commentId: UUID }>({
            query: ({ applicationId, commentId }) => ({
                url: `solicitudes/${applicationId}/comentarios/${commentId}`,
                method: "DELETE",
            }),
        }),
        patchCommentByApp: build.mutation<any, { applicationId: UUID; commentId: UUID; payload: unknown }>({
            query: ({ applicationId, commentId, payload }) => ({
                url: `solicitudes/${applicationId}/comentarios/${commentId}`,
                method: "PATCH",
                body: payload,
            }),
        }),
        getCommentsByModule: build.query<Array<Comment>, { applicationId: UUID; moduleId: UUID }>({
            query: ({ applicationId, moduleId }) => ({
                url: `solicitudes/${applicationId}/comentarios`,
                params: {
                    q: `module_id:${moduleId}`,
                },
            }),
            transformResponse: (response: ResponsePagination<Comment>) => {
                return response.data.data;
            },
        }),
        getCompanyProfile: build.query<CompanyProfile, { rfc: string; loanApplicationId: UUID }>({
            // query: ({ rfc, loanApplicationId }) => ({
            //     url: `sat_core/company_profile/${rfc}`,
            //     params: {
            //         loan_application_id: loanApplicationId,
            //     },
            // }),
            // transformResponse: (response: Response<CompanyProfile>) => {
            //     return response.data;
            // },

            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.loanApplicationId === "3912e960-7795-434f-bc47-d277ff43937e") {
                    return {
                        data: CompanyProfileData,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/company_profile/${args.rfc}`,
                    params: {
                        loan_application_id: args.loanApplicationId,
                    },
                });

                return result.data;
            },
        }),
        getCSF: build.query<Array<CertificateTax>, string>({
            // query: (params) => ({
            //     url: `sat_core/certificate_tax_status?${params}`,
            // }),
            // transformResponse: (response: ResponsePagination<CertificateTax>) => {
            //     return response.data.data;
            // },

            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("BPP2207121N2")) {
                    return {
                        data: CsfData.data,
                    };
                }
                const result = await baseQuery({
                    url: `/sat_core/certificate_tax_status?${args}`,
                });

                return result.data.data;
            },
        }),
        postStatusApplication: build.mutation<
            any,
            { applicationId: UUID; statusCode: string; userName: string; disableSequenceCheck?: boolean }
        >({
            query: ({ applicationId, statusCode, userName, disableSequenceCheck = undefined }) => ({
                url: `solicitudes/${applicationId}/progreso`,
                method: "POST",
                body: {
                    user_name: `BPM - ${userName}`,
                    status_code: statusCode,
                    disable_sequence_check: disableSequenceCheck,
                },
            }),
        }),
        getBankAccountsApplication: build.query<Array<BankAccount>, UUID>({
            // query: (applicationId) => ({
            //     url: `solicitudes/${applicationId}/cuentas_bancarias`,
            // }),
            // transformResponse: (response: Response<Array<BankAccount>>) => {
            //     return response.data;
            // },

            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args === "3912e960-7795-434f-bc47-d277ff43937e") {
                    return {
                        data: CuentasBancarias,
                    };
                }

                const result = await baseQuery({
                    url: `solicitudes/${args}/cuentas_bancarias`,
                });

                return result.data;
            },
        }),
        patchBankAccountsApplication: build.mutation<
            any,
            { applicationId: UUID; bankAccountId: UUID; payload: Partial<BankAccount> }
        >({
            query: ({ applicationId, bankAccountId, payload }) => ({
                url: `solicitudes/${applicationId}/cuentas_bancarias/${bankAccountId}`,
                method: "PATCH",
                body: payload,
            }),
        }),
        postBankAccountsApplication: build.mutation<BankAccount, { applicationId: UUID; payload: any }>({
            query: ({ applicationId, payload }) => ({
                url: `solicitudes/${applicationId}/cuentas_bancarias`,
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: Response<BankAccount>) => {
                return response.data;
            },
        }),
        deleteBankAccountApplication: build.mutation<void, { applicationId: UUID; bankAccountId: UUID }>({
            query: ({ applicationId, bankAccountId }) => ({
                url: `solicitudes/${applicationId}/cuentas_bancarias/${bankAccountId}`,
                method: "DELETE",
            }),
        }),
        getBankAccountDocuments: build.query<
            Array<BankAccountDocument>,
            { loanApplicationId: UUID; bankAccountId: UUID }
        >({
            query: ({ loanApplicationId, bankAccountId }) => ({
                url: `solicitudes/${loanApplicationId}/cuentas_bancarias/${bankAccountId}/documentos`,
            }),
            transformResponse: (response: ResponsePagination<BankAccountDocument>) => {
                return response.data.data;
            },
        }),
        postBankAccountDocument: build.mutation<
            BankAccountDocument,
            { loanApplicationId: UUID; bankAccountId: UUID; payload: Partial<BankAccountDocument> }
        >({
            query: ({ loanApplicationId, bankAccountId, payload }) => ({
                url: `solicitudes/${loanApplicationId}/cuentas_bancarias/${bankAccountId}/documentos`,
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: Response<BankAccountDocument>) => {
                return response.data;
            },
        }),
        deleteBankAccountDocument: build.mutation<
            void,
            { loanApplicationId: UUID; bankAccountId: UUID; bankAccountDocumentId: UUID }
        >({
            query: ({ loanApplicationId, bankAccountId, bankAccountDocumentId }) => ({
                url: `solicitudes/${loanApplicationId}/cuentas_bancarias/${bankAccountId}/documentos/${bankAccountDocumentId}`,
                method: "DELETE",
            }),
        }),
        patchBankAccountDocument: build.mutation<
            BankAccountDocument,
            {
                loanApplicationId: UUID;
                bankAccountId: UUID;
                bankAccountDocumentId: UUID;
                payload: Partial<BankAccountDocument>;
            }
        >({
            query: ({ loanApplicationId, bankAccountId, bankAccountDocumentId, payload }) => ({
                url: `solicitudes/${loanApplicationId}/cuentas_bancarias/${bankAccountId}/documentos/${bankAccountDocumentId}`,
                method: "PATCH",
                body: payload,
            }),
            transformResponse: (response: Response<BankAccountDocument>) => {
                return response.data;
            },
        }),
        putCondition: build.mutation<Condition, { applicationId: UUID; conditionId: UUID; body: any }>({
            query: ({ applicationId, conditionId, body }) => ({
                url: `solicitudes/${applicationId}/condiciones/${conditionId}`,
                method: "PUT",
                body: body,
            }),
            transformResponse: (response: Response<Condition>) => {
                return response.data;
            },
        }),
        postCondition: build.mutation<Condition, { applicationId: UUID; body: any }>({
            query: ({ applicationId, body }) => ({
                url: `solicitudes/${applicationId}/condiciones`,
                method: "POST",
                body: body,
            }),
            transformResponse: (response: Response<Condition>) => {
                return response.data;
            },
        }),
        getCondition: build.query<Condition, { applicationId: UUID; conditionId: UUID }>({
            query: ({ applicationId, conditionId }) => ({
                url: `solicitudes/${applicationId}/condiciones/${conditionId}`,
            }),
            transformResponse: (response: Response<Condition>) => {
                return response.data;
            },
        }),
        getConditions: build.query<Array<Condition>, { applicationId: UUID; params?: string }>({
            // query: ({ applicationId, params }) => ({
            //     url: `solicitudes/${applicationId}/condiciones?${params}`,
            // }),
            // transformResponse: (response: ResponsePagination<Condition>) => {
            //     return response.data.data;
            // },

            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.applicationId === "3912e960-7795-434f-bc47-d277ff43937e") {
                    return {
                        data: ConditionData.data,
                    };
                }
                const result = await baseQuery({
                    url: `/solicitudes/${args.applicationId}/condiciones?${args.params}`,
                });

                return result.data.data;
            },
        }),
        getCreditValidations: build.query<Array<CreditValidation>, { applicationId: UUID; params?: string }>({
            // query: ({ applicationId, params }) => ({
            //     url: `solicitudes/${applicationId}/validacion_crediticia?${params}`,
            // }),
            // transformResponse: (response: ResponsePagination<CreditValidation>) => {
            //     return response.data.data;
            // },

            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.applicationId === "3912e960-7795-434f-bc47-d277ff43937e") {
                    return {
                        data: ValidacionCreditia.data,
                    };
                }

                const result = await baseQuery({
                    url: `solicitudes/${args.applicationId}/validacion_crediticia?${args.params}`,
                });

                return result.data.data;
            },
        }),
        patchCreditValidation: build.mutation<
            any,
            { applicationId: UUID; creditValidationId: UUID; body: Partial<CreditValidation> }
        >({
            query: ({ applicationId, creditValidationId, body }) => ({
                url: `solicitudes/${applicationId}/validacion_crediticia/${creditValidationId}`,
                method: "PATCH",
                body: body,
            }),
            transformResponse: (response: Response<CreditValidation>) => {
                return response.data;
            },
        }),
        postCreditValidation: build.mutation<
            CreditValidation,
            { applicationId: UUID; payload: Partial<CreditValidation> }
        >({
            query: ({ applicationId, payload }) => ({
                url: `solicitudes/${applicationId}/validacion_crediticia`,
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: Response<CreditValidation>) => {
                return response.data;
            },
        }),
        getLegalIncidents: build.query<Array<LegalIncident>, UUID>({
            query: (applicationId) => ({
                url: `solicitudes/${applicationId}/incidencias_legales`,
            }),
            transformResponse: (response: ResponsePagination<LegalIncident>) => {
                return response.data.data;
            },
        }),
        postLegalIncident: build.mutation<any, { applicationId: UUID; body: any }>({
            query: ({ applicationId, body }) => ({
                url: `solicitudes/${applicationId}/incidencias_legales`,
                method: "POST",
                body: body,
            }),
        }),
        patchLegalIncident: build.mutation<any, { applicationId: UUID; legalIncidentId: UUID; body: any }>({
            query: ({ applicationId, legalIncidentId, body }) => ({
                url: `solicitudes/${applicationId}/incidencias_legales/${legalIncidentId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        getAnalysisVisit: build.query<Array<AnalysisVisit>, UUID>({
            query: (applicationId) => ({
                url: `solicitudes/${applicationId}/analisis_visita`,
            }),
            transformResponse: (response: ResponsePagination<AnalysisVisit>) => {
                return response.data.data;
            },
        }),
        patchAnalysisVisit: build.mutation<any, { applicationId: UUID; analysisVisitId: UUID; body: any }>({
            query: ({ applicationId, analysisVisitId, body }) => ({
                url: `solicitudes/${applicationId}/analisis_visita/${analysisVisitId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        getCheckControls: build.query<Array<CheckControl>, UUID>({
            query: (applicationId) => ({
                url: `solicitudes/${applicationId}/check_control`,
            }),
            transformResponse: (response: ResponsePagination<CheckControl>) => {
                return response.data.data;
            },
        }),
        patchCheckControl: build.mutation<any, { applicationId: UUID; checkControlId: UUID; body: any }>({
            query: ({ applicationId, checkControlId, body }) => ({
                url: `solicitudes/${applicationId}/check_control/${checkControlId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        getApplicationDetail: build.query<ApplicationDetail, UUID>({
            query: (applicationId) => ({
                url: `solicitudes/${applicationId}/detalles`,
            }),
            transformResponse: (response: Response<ApplicationDetail>) => {
                return response.data;
            },
        }),
        postApplicationDetail: build.mutation<void, { applicationId: UUID; body: any }>({
            query: ({ applicationId, body }) => ({
                url: `solicitudes/${applicationId}/detalles`,
                method: "POST",
                body: body,
            }),
        }),
        putApplicationDetail: build.mutation<void, { applicationId: UUID; detailId: UUID; body: any }>({
            query: ({ applicationId, detailId, body }) => ({
                url: `solicitudes/${applicationId}/detalles/${detailId}`,
                method: "PUT",
                body: body,
            }),
        }),
        postPaymentMethod: build.mutation<void, { applicationId: UUID; body: any }>({
            query: ({ applicationId, body }) => ({
                url: `solicitudes/${applicationId}/forma_pagos`,
                method: "POST",
                body: body,
            }),
        }),
        getApplicationPaymentMethods: build.query<Array<ApplicationPaymentMethod>, UUID>({
            query: (applicationId) => ({
                url: `solicitudes/${applicationId}/forma_pagos`,
            }),
            transformResponse: (response: Response<Array<ApplicationPaymentMethod>>) => {
                return response.data;
            },
        }),
        postDestinationResources: build.mutation<void, { applicationId: UUID; body: any }>({
            query: ({ applicationId, body }) => ({
                url: `solicitudes/${applicationId}/destino_recursos`,
                method: "POST",
                body: body,
            }),
        }),
        getDestinationResource: build.query<DestinationResource, UUID>({
            query: (applicationId) => ({
                url: `solicitudes/${applicationId}/destino_recursos`,
            }),
            transformResponse: (response: Response<DestinationResource>) => {
                return response.data;
            },
        }),
        patchDestinationResource: build.mutation<void, { applicationId: UUID; resourceUseId: UUID; body: any }>({
            query: ({ applicationId, resourceUseId, body }) => ({
                url: `solicitudes/${applicationId}/destino_recursos/${resourceUseId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        getOffers: build.query<Array<FinancialOffer>, { applicationId: UUID; filters: string }>({
            query: ({ applicationId, filters }) => ({
                url: `solicitudes/${applicationId}/ofertas?${filters}`,
            }),
            transformResponse: (response: ResponsePagination<FinancialOffer>) => {
                return response.data.data;
            },
        }),
        postFinancialOffer: build.mutation<FinancialOffer, { applicationId: UUID; body: Partial<FinancialOffer> }>({
            query: ({ applicationId, body }) => ({
                url: `solicitudes/${applicationId}/ofertas`,
                method: "POST",
                body: body,
            }),
            transformResponse: (response: Response<FinancialOffer>) => {
                return response.data;
            },
        }),
        patchFinancialOffer: build.mutation<
            FinancialOffer,
            { applicationId: UUID; financialOfferId: UUID; body: Partial<FinancialOffer> }
        >({
            query: ({ applicationId, financialOfferId, body }) => ({
                url: `solicitudes/${applicationId}/ofertas/${financialOfferId}`,
                method: "PATCH",
                body: body,
            }),
            transformResponse: (response: Response<FinancialOffer>) => {
                return response.data;
            },
        }),
        getGuarantees: build.query<Array<Guarantee>, { applicationId: UUID; params: string }>({
            query: ({ applicationId, params }) => ({
                url: `solicitudes/${applicationId}/garantias?${params}`,
            }),
            transformResponse: (response: ResponsePagination<Guarantee>) => {
                return response.data.data;
            },
        }),
        postGuarantee: build.mutation<Guarantee, { applicationId: UUID; payload: Array<Partial<Guarantee>> }>({
            query: ({ applicationId, payload }) => ({
                url: `solicitudes/${applicationId}/garantias`,
                method: "POST",
                body: {
                    guarantees_list: payload,
                },
            }),
            transformResponse: (response: Response<Guarantee>) => {
                return response.data;
            },
        }),
        patchGuarantee: build.mutation<void, { loanApplicationId: UUID; guaranteeId: UUID; body: Partial<Guarantee> }>({
            query: ({ loanApplicationId, guaranteeId, body }) => ({
                url: `solicitudes/${loanApplicationId}/garantias/${guaranteeId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        deleteGuarantee: build.mutation<void, { loanApplicationId: UUID; guaranteeId: UUID }>({
            query: ({ loanApplicationId, guaranteeId }) => ({
                url: `solicitudes/${loanApplicationId}/garantias/${guaranteeId}`,
                method: "DELETE",
            }),
        }),
        postCloneApplication: build.mutation<
            Application,
            { loanApplicationId: UUID; userTrigger: { user_id: UUID; user_name: string }; sourceCode?: string }
        >({
            query: (payload) => ({
                url: `solicitudes/${payload.loanApplicationId}/clone`,
                method: "POST",
                body: {
                    user_id: payload.userTrigger.user_id,
                    user_name: payload.userTrigger.user_name,
                    ...(payload.sourceCode !== undefined && {
                        loan_application: { source_code: payload.sourceCode },
                    }),
                },
            }),
            transformResponse: (response: Response<Application>) => {
                return response.data;
            },
        }),
        getLeasing: build.query<Array<LeasingRecord>, { loanApplicationId: UUID; params: string }>({
            query: ({ loanApplicationId, params }) => ({
                url: `solicitudes/${loanApplicationId}/leasing?${params}`,
            }),
            transformResponse: (response: ResponsePagination<LeasingRecord>) => {
                return response.data.data;
            },
        }),
        postLeasing: build.mutation<
            LeasingRecord,
            { loanApplicationId: UUID; leasings_list: Array<Partial<LeasingRecord>> }
        >({
            query: ({ loanApplicationId, leasings_list }) => ({
                url: `solicitudes/${loanApplicationId}/leasing`,
                method: "POST",
                body: { leasings_list: leasings_list },
            }),
        }),
        patchLeasing: build.mutation<
            LeasingRecord,
            { loanApplicationId: UUID; leasingId: UUID; body: Partial<LeasingRecord> }
        >({
            query: ({ loanApplicationId, leasingId, body }) => ({
                url: `solicitudes/${loanApplicationId}/leasing/${leasingId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        deleteLeasing: build.mutation<void, { loanApplicationId: UUID; leasingId: UUID }>({
            query: ({ loanApplicationId, leasingId }) => ({
                url: `solicitudes/${loanApplicationId}/leasing/${leasingId}`,
                method: "DELETE",
            }),
        }),
        getMotorbikeDetails: build.query<Array<MotorbikeDetail>, UUID>({
            query: (motorbikeDetailId) => ({
                url: `solicitudes/${motorbikeDetailId}/detalle_moto`,
            }),
            transformResponse: (response: ResponsePagination<MotorbikeDetail>) => {
                return response.data.data;
            },
        }),
        postMotorbikeDetail: build.mutation<MotorbikeDetail, { applicationId: UUID; body: Partial<MotorbikeDetail> }>({
            query: ({ applicationId, body }) => ({
                url: `solicitudes/${applicationId}/detalle_moto`,
                method: "POST",
                body: body,
            }),
            transformResponse: (response: Response<MotorbikeDetail>) => {
                return response.data;
            },
        }),
        getMotorbikeDetail: build.query<MotorbikeDetail, { applicationId: UUID; motorbikeDetailId: UUID }>({
            query: ({ applicationId, motorbikeDetailId }) => ({
                url: `solicitudes/${applicationId}/detalle_moto/${motorbikeDetailId}`,
            }),
            transformResponse: (response: Response<MotorbikeDetail>) => {
                return response.data;
            },
        }),
        patchMotorbikeDetail: build.mutation<
            MotorbikeDetail,
            { applicationId: UUID; motorbikeDetailId: UUID; body: Partial<MotorbikeDetail> }
        >({
            query: ({ applicationId, motorbikeDetailId, body }) => ({
                url: `solicitudes/${applicationId}/detalle_moto/${motorbikeDetailId}`,
                method: "PATCH",
                body: body,
            }),
            transformResponse: (response: Response<MotorbikeDetail>) => {
                return response.data;
            },
        }),
        deleteMotorbikeDetail: build.mutation<void, { applicationId: UUID; motorbikeDetailId: UUID }>({
            query: ({ applicationId, motorbikeDetailId }) => ({
                url: `solicitudes/${applicationId}/detalle_moto/${motorbikeDetailId}`,
                method: "DELETE",
            }),
        }),
    }),
});

export const {
    useLazyGetApplicationQuery,
    usePutApplicationsMutation,
    useGetScheduledVisitQuery,
    useLazyGetScheduledVisitQuery,
    usePostScheduledVisitMutation,
    usePatchScheduledVisitMutation,
    usePostLostInterestMutation,
    usePostReasonRejectAppMutation,
    useGetCommentsByAppQuery,
    useLazyGetCommentsByAppQuery,
    usePostCommentByAppMutation,
    useDeleteCommentByAppMutation,
    usePatchCommentByAppMutation,
    useLazyGetCommentsByModuleQuery,
    useGetCompanyProfileQuery,
    useLazyGetCompanyProfileQuery,
    usePostStatusApplicationMutation,
    useGetBankAccountsApplicationQuery,
    usePutConditionMutation,
    usePostConditionMutation,
    useGetConditionQuery,
    useLazyGetConditionQuery,
    useLazyGetConditionsQuery,
    usePatchBankAccountsApplicationMutation,
    useLazyGetBankAccountsApplicationQuery,
    usePostBankAccountsApplicationMutation,
    useDeleteBankAccountApplicationMutation,
    usePatchBankAccountDocumentMutation,
    useLazyGetBankAccountDocumentsQuery,
    usePostBankAccountDocumentMutation,
    useDeleteBankAccountDocumentMutation,
    useLazyGetCreditValidationsQuery,
    usePatchCreditValidationMutation,
    usePostCreditValidationMutation,
    useGetLegalIncidentsQuery,
    useLazyGetLegalIncidentsQuery,
    usePostLegalIncidentMutation,
    usePatchLegalIncidentMutation,
    useLazyGetAnalysisVisitQuery,
    usePatchAnalysisVisitMutation,
    useLazyGetCheckControlsQuery,
    usePatchCheckControlMutation,
    useLazyGetCSFQuery,
    useLazyGetApplicationDetailQuery,
    usePutApplicationDetailMutation,
    usePostApplicationDetailMutation,
    usePostPaymentMethodMutation,
    useLazyGetApplicationPaymentMethodsQuery,
    usePostDestinationResourcesMutation,
    useLazyGetDestinationResourceQuery,
    usePatchDestinationResourceMutation,
    useLazyGetApplicationResumeQuery,
    useLazyGetApplicationResumeByCompanyIdQuery,
    useLazyGetApplicationsMarketplaceResumeQuery,
    useLazyGetOffersQuery,
    usePostFinancialOfferMutation,
    usePatchFinancialOfferMutation,
    useLazyGetGuaranteesQuery,
    usePostGuaranteeMutation,
    usePatchGuaranteeMutation,
    useDeleteGuaranteeMutation,
    usePostCloneApplicationMutation,
    useLazyGetLeasingQuery,
    usePostLeasingMutation,
    usePatchLeasingMutation,
    useDeleteLeasingMutation,
    useLazyGetMotorbikeDetailsQuery,
    usePostMotorbikeDetailMutation,
    useLazyGetMotorbikeDetailQuery,
    usePatchMotorbikeDetailMutation,
    useDeleteMotorbikeDetailMutation,
} = applicationsApi;
